import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Button from '../../components/Button';
import Icon from '../../components/AppIcon';
import { trackHeroVideoComplete } from '../../utils/analytics';

const HeroSection = ({ onJoinWaitlist, onTryDemo, waitlistCount = 1247 }) => {
  const { t } = useTranslation();
  // Mobile/Tablet width to keep items on a single line and align icons/text
  const firstRowContainerRef = React.useRef(null);
  const secondRowContainerRef = React.useRef(null);
  const thirdRowContainerRef = React.useRef(null);
  const [mobilePropsWidth, setMobilePropsWidth] = React.useState(null);

  React.useEffect(() => {
    const measure = () => {
      if (typeof window !== 'undefined' && window.innerWidth < 1024) {
        // Measure natural widths (before fixed width is applied)
        const w1 = firstRowContainerRef.current ? (firstRowContainerRef.current.scrollWidth || firstRowContainerRef.current.offsetWidth || 0) : 0;
        const w2 = secondRowContainerRef.current ? (secondRowContainerRef.current.scrollWidth || secondRowContainerRef.current.offsetWidth || 0) : 0;
        const w3 = thirdRowContainerRef.current ? (thirdRowContainerRef.current.scrollWidth || thirdRowContainerRef.current.offsetWidth || 0) : 0;
        const natural = Math.max(w1, w2, w3) || null;
        const clamp = natural ? Math.min(natural, Math.floor(window.innerWidth * 0.92)) : null; // prevent overflow on very small screens
        const w = clamp;
        setMobilePropsWidth(w);
      } else {
        setMobilePropsWidth(null);
      }
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });
    return () => window.removeEventListener('resize', measure);
  }, []);

  const content = {
    title: t('hero.title'),
    description: t('hero.description'),
    // Order: prop2 (AI Translation), prop3 (Communities), prop1 (Verified Services)
    props: [
      {
        icon: 'MessageCircle',
        title: t('hero.prop2'),
      },
      {
        icon: 'Users',
        title: t('hero.prop3'),
      },
      {
        icon: 'Shield',
        title: t('hero.prop1'),
      },
    ],
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden w-full">
      {/* Mobile Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        onEnded={trackHeroVideoComplete}
        className="absolute inset-0 w-screen left-1/2 -translate-x-1/2 h-full object-cover md:hidden pointer-events-none"
      >
        <source src="/hero_movie_mobile.mp4" type="video/mp4" />
      </video>
      
      {/* Background Video for Desktop/Tablet */}
      <video
        autoPlay
        loop
        muted
        playsInline
        onEnded={trackHeroVideoComplete}
        className="absolute inset-0 w-screen left-1/2 -translate-x-1/2 h-full object-cover hidden md:block pointer-events-none"
      >
        <source src="/hero_movie.mp4" type="video/mp4" />
      </video>
      
      {/* Fallback gradient overlay for video */}
      <div
        className="absolute inset-0 w-screen left-1/2 -translate-x-1/2 hidden md:block pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.3) 100%)'
        }}
      ></div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Primary Headline */}
        <motion.h1
          className="font-bold text-white mb-6 leading-tight"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="text-2xl md:text-3xl lg:text-5xl block mb-6 font-extrabold">{content.title}</span>
          {/* <span className="text-4xl md:text-5xl block mb-3 font-medium">&</span> */}
          <span className="text-4xl md:text-5xl lg:text-7xl text-white/90 flex items-center justify-center gap-4 sm:gap-7 font-black max-w-[90vw] mx-auto">
            <span className="tracking-tight whitespace-pre break-words">{content.description.replace(' ', '  ')}</span>
            <img 
              src="/logo-hero.png"
              alt="logl" 
              className="h-14 md:h-20 w-auto"
            />
          </span>
        </motion.h1>


        {/* Value Props */}
        <motion.div
          className="flex flex-col lg:flex-row items-center justify-center gap-6 sm:gap-8 md:gap-12 mt-20 md:mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          {/* Row 1: centered by text on mobile; 3-column grid keeps icon baseline */}
          <div
            ref={firstRowContainerRef}
            className="mx-auto text-white/90"
            style={{ width: mobilePropsWidth ? `${mobilePropsWidth}px` : undefined, gridTemplateColumns: '20px 1fr 20px', display: 'grid', alignItems: 'center', columnGap: '0.5rem' }}
          >
            <div className="flex items-center justify-center"><Icon name={content.props[0].icon} size={20} className="text-green-400" /></div>
            <span className="text-sm font-medium text-left sm:text-left whitespace-nowrap">{content.props[0].title}</span>
            <div />
          </div>
          {/* Row 2: align left to row 1 icon on mobile */}
          <div
            ref={secondRowContainerRef}
            className="mx-auto text-white/90"
            style={{ width: mobilePropsWidth ? `${mobilePropsWidth}px` : undefined, gridTemplateColumns: '20px 1fr 20px', display: 'grid', alignItems: 'center', columnGap: '0.5rem' }}
          >
            <div className="flex items-center justify-center"><Icon name={content.props[1].icon} size={20} className="text-blue-400" /></div>
            <span className="text-sm font-medium text-left sm:text-left whitespace-nowrap">{content.props[1].title}</span>
            <div />
          </div>
          {/* Row 3: align left to row 1 icon on mobile */}
          <div
            ref={thirdRowContainerRef}
            className="mx-auto text-white/90"
            style={{ width: mobilePropsWidth ? `${mobilePropsWidth}px` : undefined, gridTemplateColumns: '20px 1fr 20px', display: 'grid', alignItems: 'center', columnGap: '0.5rem' }}
          >
            <div className="flex items-center justify-center"><Icon name={content.props[2].icon} size={20} className="text-purple-400" /></div>
            <span className="text-sm font-medium text-left sm:text-left whitespace-nowrap">{content.props[2].title}</span>
            <div />
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <Icon name="ChevronDown" size={24} className="text-white/70" />
      </motion.div>
    </div>
  );
};

export default HeroSection;